import { useState } from 'react'
import { Alert, Button, Card, Form, Input, Tag } from 'antd'
import { CheckOutlined, CreditCardOutlined } from '@ant-design/icons'
import { getFeatureTheme } from '@/lib/productBranding'
import { useToast } from '@/contexts/ToastContext'
import {
  ADVICEPREP_DEFAULT_CONNECTIONS,
  advicePrepCommittedMonthly,
  advicePrepCouponCopy,
  advicePrepDueToday,
  advicePrepTrialEnd,
  advicePrepTrialMonths,
  formatAdvicePrepCurrency,
  type AdvicePrepAppliedCoupon,
} from '../pricing'
import { useValidateAdvicePrepCouponMutation } from '../hooks'
import AdvicePrepPromoModal from './AdvicePrepPromoModal'
import AdvicePrepTrialTerms from './AdvicePrepTrialTerms'
import ConnectionsEstimator from './ConnectionsEstimator'

const FEATURES = [
  'Ready in 4 steps: Agenda → Data → AI Analysis → Outputs',
  'Branded Full Advisory Report, Executive Summary, Slide deck, and Pre-meeting email',
  'Video Avatar Summary — 90-second personalised export',
  'Consolidated Analysis for franchisees and subsidiary groups',
  'In-app assistant (Preppy) with guided walkthroughs',
  'Xero read-only, daily refresh, 60-day token cycle',
]

const INVALID_COUPON_MESSAGE = 'You have entered an invalid Promo Code'

function formatTrialEnd(months: number) {
  return advicePrepTrialEnd(months).toLocaleDateString()
}

type AdvicePrepUpsellCardProps = {
  hasPaymentMethod: boolean
  subscribing: boolean
  onSubscribe: (promoCode: string) => void
  onRequestPaymentMethod: (promoCode: string) => void
  disabled?: boolean
}

export default function AdvicePrepUpsellCard({
  hasPaymentMethod,
  subscribing,
  onSubscribe,
  onRequestPaymentMethod,
  disabled,
}: AdvicePrepUpsellCardProps) {
  const theme = getFeatureTheme('advisoryIntel')
  const { addToast } = useToast()
  const [connections, setConnections] = useState(ADVICEPREP_DEFAULT_CONNECTIONS)
  const [couponCode, setCouponCode] = useState('')
  const [appliedCoupon, setAppliedCoupon] = useState<AdvicePrepAppliedCoupon | null>(null)
  const [couponError, setCouponError] = useState<string | null>(null)
  const [promoModalOpen, setPromoModalOpen] = useState(false)
  const validateCoupon = useValidateAdvicePrepCouponMutation()

  const trialMonths = advicePrepTrialMonths(appliedCoupon)
  const trialEndLabel = trialMonths ? formatTrialEnd(trialMonths) : null
  const discountCopy = trialMonths ? null : advicePrepCouponCopy(appliedCoupon)
  const dueToday = advicePrepDueToday(!!appliedCoupon, appliedCoupon)
  const busy = validateCoupon.isPending || subscribing

  /**
   * Resolves to the coupon to subscribe with, or null when the flow must stop.
   * A code still sitting unapplied in the field is checked here rather than
   * trusted, so no unverified code can reach the card modal or the subscribe
   * call — a rejected one would otherwise bill at full price behind a screen
   * that just promised a free trial.
   */
  const resolveCoupon = async (candidate?: string): Promise<AdvicePrepAppliedCoupon | null> => {
    // Taken as an argument, not read back from state: the modal sets the field
    // and validates in the same tick, before a state update has landed.
    const entered = (candidate ?? couponCode).trim()
    if (appliedCoupon || !entered) return appliedCoupon

    try {
      const coupon = await validateCoupon.mutateAsync(entered)
      if (!coupon) {
        setAppliedCoupon(null)
        setCouponError(INVALID_COUPON_MESSAGE)
        return null
      }
      setAppliedCoupon(coupon)
      setCouponError(null)
      return coupon
    } catch (e) {
      setCouponError(e instanceof Error ? e.message : 'Unable to check that promo code')
      return null
    }
  }

  const handleApply = async () => {
    if (!couponCode.trim()) {
      setCouponError('Enter a promo code, or leave the field empty to subscribe without one.')
      return
    }
    const coupon = await resolveCoupon()
    if (coupon) addToast('Promo code applied', 'success')
  }

  const proceed = (coupon: AdvicePrepAppliedCoupon | null) => {
    const promoCode = coupon?.code ?? ''
    if (hasPaymentMethod) onSubscribe(promoCode)
    else onRequestPaymentMethod(promoCode)
  }

  /**
   * Nothing in the field and no code applied means the customer has not been
   * asked yet, so the modal asks before the subscription call rather than
   * letting someone holding a code scroll past the field and pay in full.
   *
   * A code already typed inline is validated here instead, and stops either
   * way: the button said "subscribe" when it was pressed, so the trial terms it
   * just unlocked are confirmed on a second click.
   */
  const handleSubscribe = async () => {
    if (appliedCoupon) {
      proceed(appliedCoupon)
      return
    }

    if (!couponCode.trim()) {
      setCouponError(null)
      setPromoModalOpen(true)
      return
    }

    await resolveCoupon()
  }

  const handleRemove = () => {
    setAppliedCoupon(null)
    setCouponCode('')
    setCouponError(null)
  }

  const closePromoModal = () => {
    setPromoModalOpen(false)
    setCouponError(null)
  }

  const subscribeLabel = trialMonths
    ? hasPaymentMethod
      ? `Start ${trialMonths} month free trial`
      : 'Add payment method and start trial'
    : hasPaymentMethod
      ? `Subscribe for ${formatAdvicePrepCurrency(advicePrepCommittedMonthly())}/month`
      : 'Add payment method and subscribe'

  return (
    <Card className="rounded-xl border" style={{ borderColor: theme.primary, background: theme.surface }}>
      <Tag className="mb-2 rounded-full border-none px-3 py-0.5 text-xs font-semibold" style={{ background: theme.primary, color: '#ffffff' }}>
        New — available now
      </Tag>

      <div className="font-display text-lg font-bold text-gray-900">Add AdvicePrep to your subscription</div>
      <p className="mt-1 max-w-[46rem] text-sm leading-6 text-gray-600">
        AI-driven meeting prep for Xero clients. Runs analysis, drafts talking points, and builds branded client
        packs — in minutes, not hours.
      </p>

      <ul className="mt-4 grid list-none grid-cols-1 gap-y-2 p-0 md:grid-cols-2 md:gap-x-8">
        {FEATURES.map((feature) => (
          <li key={feature} className="flex items-start gap-2 text-[13px] text-gray-700">
            <CheckOutlined className="mt-1 shrink-0 text-xs" style={{ color: theme.strong }} />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <div className="mt-5 rounded-xl border bg-white/70 p-4" style={{ borderColor: theme.primary }}>
        <div className="mb-3 font-display text-sm font-semibold" style={{ color: theme.strong }}>
          Estimate your price
        </div>
        <ConnectionsEstimator connections={connections} onChange={setConnections} accent={theme.primary} />
      </div>

      <div className="mt-4 rounded-xl border bg-white/70 p-4" style={{ borderColor: theme.primary }}>
        <div className="mb-3 font-display text-sm font-semibold" style={{ color: theme.strong }}>
          Promo Code
        </div>

        {appliedCoupon ? (
          <Alert
            type="success"
            showIcon
            message={
              trialMonths
                ? `${appliedCoupon.code} applied — ${trialMonths} month free trial`
                : `${appliedCoupon.code} applied`
            }
            description={
              <AdvicePrepTrialTerms
                trialMonths={trialMonths}
                dueToday={dueToday}
                trialEndLabel={trialEndLabel}
                discountSentence={discountCopy?.applied ?? null}
              />
            }
            action={
              <Button size="small" disabled={busy || disabled} onClick={handleRemove}>
                Remove
              </Button>
            }
          />
        ) : (
          <Form layout="vertical" onFinish={() => void handleApply()}>
            <Form.Item
              className="mb-0"
              validateStatus={couponError ? 'error' : undefined}
              help={
                couponError ?? 'A promo code is what starts a free trial. Without one, billing begins today.'
              }
            >
              <div className="flex flex-wrap items-start gap-2">
                <Input
                  id="couponCode"
                  className="w-full sm:w-80"
                  value={couponCode}
                  disabled={busy || disabled}
                  placeholder="Please enter a coupon code if available"
                  aria-label="Promo Code"
                  onChange={(e) => {
                    setCouponCode(e.target.value)
                    setCouponError(null)
                  }}
                />
                <Button htmlType="submit" loading={validateCoupon.isPending} disabled={subscribing || disabled}>
                  Apply
                </Button>
              </div>
            </Form.Item>
          </Form>
        )}
      </div>

      {!hasPaymentMethod && (
        <Alert
          type="info"
          showIcon
          className="mt-4"
          message="No payment method on file"
          description={
            trialMonths
              ? 'Your promo code has already been checked. You will be asked for a card next, then the trial starts — nothing is charged until it ends.'
              : 'You will be asked for a card first, then subscribed straight away. AdvicePrep is charged to the same card as your other subscriptions.'
          }
        />
      )}

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Button
          type="primary"
          size="large"
          icon={hasPaymentMethod ? undefined : <CreditCardOutlined />}
          loading={busy}
          disabled={disabled}
          onClick={() => void handleSubscribe()}
        >
          {subscribeLabel}
        </Button>
        <span className="text-xs text-gray-500">
          {trialMonths && trialEndLabel
            ? `No charge today. Billing starts ${trialEndLabel} unless you cancel before then.`
            : 'Flat fee billed monthly in AUD, ex-GST. Connections beyond five are billed on usage.'}
        </span>
      </div>

      <AdvicePrepPromoModal
        open={promoModalOpen}
        validating={validateCoupon.isPending}
        error={couponError}
        applied={appliedCoupon}
        trialMonths={trialMonths}
        dueToday={dueToday}
        trialEndLabel={trialEndLabel}
        discountSentence={discountCopy?.applied ?? null}
        onApply={(code) => {
          setCouponCode(code)
          void resolveCoupon(code)
        }}
        onBack={handleRemove}
        onConfirm={() => {
          setPromoModalOpen(false)
          proceed(appliedCoupon)
        }}
        onSkip={() => {
          closePromoModal()
          proceed(null)
        }}
        onCancel={closePromoModal}
      />
    </Card>
  )
}
